import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { Bell, Loader2, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { ALABAMA_COUNTIES } from "@/lib/alabamaCounties";

const SECTORS = [
  "Health & Wellness", "Public Safety", "Education", "Infrastructure",
  "Housing", "Economic Development", "Agriculture", "Environment",
];

const WatchSchema = z.object({
  name: z.string().trim().min(2, "Give the watch a name").max(80),
  county: z.string().max(60).optional().nullable(),
  sector: z.string().max(60).optional().nullable(),
  keywords: z.string().max(400).optional(),
  frequency: z.enum(["daily", "weekly"]),
});

interface Watch {
  id: string;
  name: string;
  county: string | null;
  sector: string | null;
  keywords: string[];
  frequency: "daily" | "weekly";
  active: boolean;
  last_sent_at: string | null;
  created_at: string;
}

export default function IntelAlerts() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [watches, setWatches] = useState<Watch[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    county: "",
    sector: "",
    keywords: "",
    frequency: "weekly" as "daily" | "weekly",
  });

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth?redirect=/alerts");
  }, [authLoading, user, navigate]);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("intel_watches")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Could not load watches", description: error.message, variant: "destructive" });
    } else {
      setWatches((data ?? []) as Watch[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const parsed = WatchSchema.safeParse(form);
    if (!parsed.success) {
      toast({ title: "Please check the form", description: parsed.error.issues[0]?.message ?? "Invalid input", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const keywords = (parsed.data.keywords ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 12);
    const { error } = await supabase.from("intel_watches").insert({
      user_id: user.id,
      name: parsed.data.name,
      county: parsed.data.county || null,
      sector: parsed.data.sector || null,
      keywords,
      frequency: parsed.data.frequency,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Could not save", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Watch saved", description: "You'll get a digest on the schedule you chose." });
    setForm({ name: "", county: "", sector: "", keywords: "", frequency: "weekly" });
    load();
  };

  const toggle = async (w: Watch) => {
    const { error } = await supabase.from("intel_watches").update({ active: !w.active }).eq("id", w.id);
    if (error) {
      toast({ title: "Could not update", description: error.message, variant: "destructive" });
      return;
    }
    load();
  };

  const remove = async (w: Watch) => {
    if (!confirm(`Delete watch "${w.name}"?`)) return;
    const { error } = await supabase.from("intel_watches").delete().eq("id", w.id);
    if (error) {
      toast({ title: "Could not delete", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Watch deleted" });
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Custom alerts — The Emerging Market</title>
        <meta name="description" content="Get email digests when Alabama legislation or grant programs match your county, sector, and keywords." />
        <link rel="canonical" href="https://emrkt.co/alerts" />
      </Helmet>
      <SiteHeader />
      <main className="container max-w-4xl mx-auto px-4 py-12 md:py-16">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="h-5 w-5 text-primary" />
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">Custom alerts</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground">Watches</h1>
          <p className="mt-3 text-muted-foreground text-lg">
            Create a watch to get an email digest whenever Alabama legislation
            or grant programs match your county, sector, or keywords. Delete or
            pause any watch at any time.
          </p>
        </div>

        <form onSubmit={onCreate} className="rounded-2xl border border-border bg-card p-6 mb-10 space-y-4">
          <h2 className="font-display text-lg font-semibold">New watch</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="w-name">Name *</Label>
              <Input id="w-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mobile housing bills" />
            </div>
            <div>
              <Label htmlFor="w-freq">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v: "daily" | "weekly") => setForm({ ...form, frequency: v })}>
                <SelectTrigger id="w-freq"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="w-county">County</Label>
              <Select value={form.county || "__any"} onValueChange={(v) => setForm({ ...form, county: v === "__any" ? "" : v })}>
                <SelectTrigger id="w-county"><SelectValue placeholder="Any county" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any county</SelectItem>
                  {ALABAMA_COUNTIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="w-sector">Sector</Label>
              <Select value={form.sector || "__any"} onValueChange={(v) => setForm({ ...form, sector: v === "__any" ? "" : v })}>
                <SelectTrigger id="w-sector"><SelectValue placeholder="Any sector" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any sector</SelectItem>
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="w-kw">Keywords (comma-separated)</Label>
            <Input id="w-kw" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="broadband, rural, cooperative" />
            <p className="text-xs text-muted-foreground mt-1">Matches bill or grant title/description. Up to 12 keywords.</p>
          </div>
          <Button type="submit" disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Save watch
          </Button>
        </form>

        <h2 className="font-display text-lg font-semibold mb-4">Your watches</h2>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : watches.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No watches yet. Create one above and we'll email you when matching bills or grants appear.
          </div>
        ) : (
          <ul className="space-y-3">
            {watches.map((w) => (
              <li key={w.id} className="rounded-xl border border-border bg-card p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-display font-semibold text-foreground">{w.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground rounded-full bg-muted px-2 py-0.5">
                      {w.frequency}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                    {w.county && <span>County: {w.county}</span>}
                    {w.sector && <span>Sector: {w.sector}</span>}
                    {w.keywords.length > 0 && <span>Keywords: {w.keywords.join(", ")}</span>}
                    {w.last_sent_at && <span>Last sent: {new Date(w.last_sent_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={w.active} onCheckedChange={() => toggle(w)} aria-label="Active" />
                  <Button size="icon" variant="ghost" onClick={() => remove(w)} aria-label="Delete watch">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <p className="text-xs text-muted-foreground mt-8">
          Alerts run against the same pipeline documented on{" "}
          <Link to="/methodology" className="text-primary underline">Methodology</Link>. Suppress the whole alert
          stream any time under{" "}
          <Link to="/coach" className="text-primary underline">Coach preferences</Link>.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}